/**
 * Profile / tenant-form field validators.
 *
 * Each `validate*` function returns:
 *   - `null` when the value is acceptable (including empty — empty
 *     means "clear this field", which is valid in our PATCH model).
 *   - A short, human-readable error string otherwise.
 *
 * Patterns mirror those in `api/app/schemas/validation.py`. They are
 * strict enough to reject "garbage" — phone fields can't contain
 * letters, postal codes must include a digit, city/state/country must
 * be Latin letters (with the usual punctuation) and must NOT contain
 * digits. The frontend validates on every keystroke so errors appear
 * immediately; the server enforces the same rules so we don't trust
 * client validation alone.
 *
 * `filterPhoneInput` strips disallowed characters before they ever
 * reach component state — type 'a' into the Phone field and nothing
 * happens, which is the UX the user asked for.
 */

// Lead-in `[\d(]` accepts both "+1 555..." and "(555)..." styles.
const PHONE_RE = /^\+?[\d(][\d\s\-().]{5,38}$/;
const POSTAL_RE = /^(?=.*\d)[A-Za-z0-9][A-Za-z0-9 \-]{2,11}$/;
// Latin-1 Supplement minus the math symbols at U+00D7 (×) and U+00F7 (÷).
// Literal space (not \s) so newlines/tabs are rejected — same anti-
// injection guard the backend regex enforces.
const PLACE_RE = /^[A-Za-zÀ-ÖØ-öø-ÿ][A-Za-zÀ-ÖØ-öø-ÿ \-'.]{1,79}$/;
const WEBSITE_RE = /^https?:\/\/\S+$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Characters that may appear in a phone field. Anything else is
// stripped on input so the user can't even type a letter.
const PHONE_ALLOWED = /[^0-9+\s\-().]/g;

export function filterPhoneInput(v: string): string {
  return v.replace(PHONE_ALLOWED, '');
}

export function validatePhone(v: string): string | null {
  const trimmed = v.trim();
  if (trimmed === '') return null;
  return PHONE_RE.test(trimmed)
    ? null
    : 'digits only (with +, space, dash, parens or dot) — min 7 digits';
}

export function validatePostalCode(v: string): string | null {
  const trimmed = v.trim();
  if (trimmed === '') return null;
  return POSTAL_RE.test(trimmed)
    ? null
    : 'letters and digits, must include at least one digit (3–12 chars)';
}

export function validatePlace(v: string): string | null {
  const trimmed = v.trim();
  if (trimmed === '') return null;
  return PLACE_RE.test(trimmed)
    ? null
    : "letters only (with space, hyphen, apostrophe or period); no digits";
}

export function validateWebsite(v: string): string | null {
  const trimmed = v.trim();
  if (trimmed === '') return null;
  return WEBSITE_RE.test(trimmed) ? null : 'must start with http:// or https://';
}

export function validateEmail(v: string): string | null {
  const trimmed = v.trim();
  if (trimmed === '') return null;
  return EMAIL_RE.test(trimmed) ? null : 'invalid email address';
}

/** Field-name → validator. Used by Profile.tsx and TenantFormDialog. */
export const userProfileValidators = {
  phone: validatePhone,
  mobile: validatePhone,
  city: validatePlace,
  state_region: validatePlace,
  postal_code: validatePostalCode,
  country: validatePlace,
} as const;

export const tenantProfileValidators = {
  contact_email: validateEmail,
  phone: validatePhone,
  fax: validatePhone,
  website: validateWebsite,
  city: validatePlace,
  state_region: validatePlace,
  postal_code: validatePostalCode,
  country: validatePlace,
} as const;

export type UserProfileField = keyof typeof userProfileValidators;
export type TenantProfileField = keyof typeof tenantProfileValidators;

/** Phone-like fields whose input should be filtered live. */
export const PHONE_LIKE_FIELDS = new Set<string>(['phone', 'mobile', 'fax']);
